import React from "react";
import { injectIntl } from "react-intl";
import { MessageFilled, PictureFilled, WhatsAppOutlined } from "@ant-design/icons";
import { formatTime, hourMinuteOnly } from "../../../../util/helpers";
import {
    avatarColour,
    conversationChannel,
    conversationName,
    formatWait,
    initialsOf,
    waitingMs,
    waitLevel
} from "./inbox";

const CHANNEL_LABEL = {whatsapp: 'WhatsApp', sms: 'SMS', mms: 'MMS'};
// The channel badge on the avatar is an icon, not just a colour.
const CHANNEL_ICON = {whatsapp: WhatsAppOutlined, sms: MessageFilled, mms: PictureFilled};

// One conversation in the inbox list: avatar with the channel badge, name, last message, time — or, in the
// queue, how long the customer has been waiting and a "Take" button.
const InboxRow = ({intl, conversation, selected, queued, now, onSelect, onTake, rowRef}) => {
    const t = (id, defaultMessage, values) => intl.formatMessage({id, defaultMessage}, values);
    const name = conversationName(conversation);
    const channel = conversationChannel(conversation);
    const last = conversation.lastMessage;
    const unread = !conversation.isRead;
    const wait = queued ? waitingMs(conversation, now) : null;

    const preview = !last ? '' : last.channel === 'system'
        ? t(last.body, last.body)
        : (last.body || (last.mediaUrl || last.attachments ? t('chats.attachment', 'Attachment') : ''));
    const time = !last ? '' : new Date(last.time).toDateString() === new Date(now).toDateString()
        ? hourMinuteOnly(last.time)
        : intl.formatDate(last.time, {day: 'numeric', month: 'short', ...(new Date(last.time).getFullYear() === new Date().getFullYear() ? {} : {year: 'numeric'})});

    return (
        <div
            ref={rowRef}
            role="option"
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            className={`inbox-row${selected ? ' is-selected' : ''}${unread ? ' is-unread' : ''}`}
            onClick={() => onSelect(conversation)}
            onKeyDown={e => {
                if (e.key === 'Enter' && e.target === e.currentTarget) onSelect(conversation);
            }}
        >
            <span className="inbox-row__avatar" style={{background: avatarColour(conversation.clientNumber)}} aria-hidden="true">
                {initialsOf(conversation)}
                {channel && CHANNEL_ICON[channel] ? (
                    <span className={`inbox-row__channel inbox-row__channel--${channel}`}>
                        {React.createElement(CHANNEL_ICON[channel])}
                    </span>
                ) : null}
            </span>
            <span className="inbox-row__body">
                <span className="inbox-row__top">
                    <span className="inbox-row__name" title={name}>{name}</span>
                    {wait !== null ? (
                        <span className={`inbox-row__wait is-${waitLevel(wait)}`} title={t('chats.waitingSince', 'Waiting since {time}', {time: formatTime(last.time)})}>
                            {formatWait(intl, wait)}
                        </span>
                    ) : (
                        <span className="inbox-row__time" title={last ? formatTime(last.time) : undefined}>{time}</span>
                    )}
                </span>
                <span className="inbox-row__preview">
                    {channel ? <span className="sr-only">{CHANNEL_LABEL[channel]}: </span> : null}
                    {preview}
                </span>
            </span>
            {queued && onTake ? (
                <button
                    type="button"
                    className="inbox-row__take"
                    aria-label={t('chats.takeAria', 'Take the conversation with {name}', {name})}
                    onClick={e => {
                        e.stopPropagation();
                        onTake(conversation);
                    }}
                >
                    {t('chats.take', 'Take')}
                </button>
            ) : null}
            {unread ? <span className="inbox-row__dot" aria-label={t('chats.unread', 'Unread')}/> : null}
        </div>
    );
};

export default injectIntl(InboxRow);
